/**
 * OnboardingWizard — First-time user experience.
 *
 * Collects core financial inputs across 5 focused steps,
 * then hands off to RevealStep for the "aha moment."
 *
 * Lives in features/onboarding/ — self-contained module.
 * Uses useApp() for theme colors and upM() to commit data on completion.
 */
import { useState, useCallback } from "react";
import { Slider } from '../../components/Slider';
import { useApp } from '../../hooks/useApp';
import RevealStep from './RevealStep';

/* ─── Default onboarding values (before user touches anything) ─── */
const ONBOARDING_DEFAULTS = {
  age: 22,
  startAge: 24,
  maxAge: 40,
  salary: 75000,
  salGr: 0.06,
  bonusR: 0.10,
  liv: 36000,
  nw0: 5000,
  savingsTarget: 25000,
  ret: 0.08,
};

/* ═══════════════════════════════════════════════════
   STEP COMPONENTS
   ═══════════════════════════════════════════════════ */

function WelcomeStep({ c, onNext }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 480 }}>
      <div style={{
        fontSize: 48, marginBottom: 20, lineHeight: 1,
        filter: "grayscale(0)",
      }}>
        🚀
      </div>
      <h1 style={{
        fontSize: 36, fontWeight: 800, letterSpacing: "-0.04em",
        color: c.fg, margin: "0 0 16px", lineHeight: 1.15,
      }}>
        Let's Build Your<br />Financial Future
      </h1>
      <p style={{
        fontSize: 16, color: c.muted, lineHeight: 1.6,
        margin: "0 0 40px", maxWidth: 380, marginLeft: "auto", marginRight: "auto",
      }}>
        Answer a few quick questions and we'll project your path
        to wealth. Takes under 2 minutes.
      </p>
      <button onClick={onNext} style={{
        padding: "14px 44px", borderRadius: 14, border: "none",
        background: c.blue, color: "#fff", fontSize: 16, fontWeight: 700,
        cursor: "pointer", letterSpacing: "-0.01em",
        boxShadow: `0 4px 20px ${c.blue}40`,
        transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
      }}>
        Get Started
      </button>
      <div style={{ fontSize: 12, color: c.muted, marginTop: 16, opacity: 0.6 }}>
        You can adjust everything later
      </div>
    </div>
  );
}

function ProfileStep({ data, onChange, c }) {
  return (
    <div style={{ width: "100%", maxWidth: 480 }}>
      <StepHeader
        title="About You"
        subtitle="These basics shape your entire projection timeline."
        c={c}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Slider l="Your Age" v={data.age} fn={(v) => onChange({ age: v })}
          min={18} max={35} c={c} />
        <Slider l="Career Start Age" v={data.startAge} fn={(v) => onChange({ startAge: v })}
          min={22} max={28} c={c} />
        <Slider l="Plan To Age" v={data.maxAge} fn={(v) => onChange({ maxAge: v })}
          min={35} max={65} c={c} />
      </div>
      <Hint c={c}>
        Planning to age {data.maxAge} gives you a {data.maxAge - data.age}-year runway.
      </Hint>
    </div>
  );
}

function CareerStep({ data, onChange, c }) {
  return (
    <div style={{ width: "100%", maxWidth: 480 }}>
      <StepHeader
        title="Your Career Path"
        subtitle="Where you're headed matters more than where you are."
        c={c}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Slider l="Expected Base Salary" v={data.salary} fn={(v) => onChange({ salary: v })}
          min={40000} max={200000} step={5000} f="$" c={c} />
        <Slider l="Annual Raise" v={data.salGr} fn={(v) => onChange({ salGr: v })}
          min={0.02} max={0.12} step={0.005} f="%" c={c} />
        <Slider l="Bonus Rate" v={data.bonusR} fn={(v) => onChange({ bonusR: v })}
          min={0} max={0.30} step={0.01} f="%" c={c} />
      </div>
      <Hint c={c}>
        Use your expected salary after graduation, not current income.
      </Hint>
    </div>
  );
}

function LifestyleStep({ data, onChange, c }) {
  const monthly = Math.round(data.liv / 12);
  return (
    <div style={{ width: "100%", maxWidth: 480 }}>
      <StepHeader
        title="Your Lifestyle"
        subtitle="What does your life cost? Rent, food, transport, subscriptions — all of it."
        c={c}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Slider l="Annual Living Expenses" v={data.liv} fn={(v) => onChange({ liv: v })}
          min={18000} max={72000} step={1000} f="$" c={c} />
      </div>
      <div style={{
        marginTop: 20, padding: "16px 20px", borderRadius: 14,
        background: c.card2, display: "flex", justifyContent: "space-between",
        alignItems: "center",
      }}>
        <span style={{ fontSize: 13, color: c.muted, fontWeight: 500 }}>That's roughly</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: c.blue, fontVariantNumeric: "tabular-nums" }}>
          ${monthly.toLocaleString()}/mo
        </span>
      </div>
      <Hint c={c}>
        Don't stress about precision — you'll fine-tune this in the Budget tab.
      </Hint>
    </div>
  );
}

function SavingsStep({ data, onChange, c }) {
  return (
    <div style={{ width: "100%", maxWidth: 480 }}>
      <StepHeader
        title="Savings & Growth"
        subtitle="Your current position and investing plan."
        c={c}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Slider l="Current Net Worth" v={data.nw0} fn={(v) => onChange({ nw0: v })}
          min={0} max={200000} step={1000} f="$" c={c} />
        <Slider l="Annual Savings Target" v={data.savingsTarget} fn={(v) => onChange({ savingsTarget: v })}
          min={5000} max={100000} step={5000} f="$" c={c} />
        <Slider l="Expected Return" v={data.ret} fn={(v) => onChange({ ret: v })}
          min={0.04} max={0.15} step={0.005} f="%" c={c} />
      </div>
      <Hint c={c}>
        A diversified portfolio (like XEQT) has historically returned ~8–10%.
      </Hint>
    </div>
  );
}

/* ─── Shared sub-components ─── */

function StepHeader({ title, subtitle, c }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{
        fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em",
        color: c.fg, margin: 0, lineHeight: 1.2,
      }}>
        {title}
      </h2>
      <p style={{
        fontSize: 14, color: c.muted, margin: "10px 0 0", lineHeight: 1.5,
      }}>
        {subtitle}
      </p>
    </div>
  );
}

function Hint({ children, c }) {
  return (
    <div style={{
      fontSize: 12, color: c.muted, marginTop: 20,
      padding: "12px 16px", borderRadius: 12,
      background: c.card2, lineHeight: 1.5, opacity: 0.8,
    }}>
      💡 {children}
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   WIZARD ORCHESTRATOR
   ═══════════════════════════════════════════════════ */

const INPUT_STEPS = [
  { id: "welcome",  Component: WelcomeStep },
  { id: "profile",  Component: ProfileStep },
  { id: "career",   Component: CareerStep },
  { id: "lifestyle", Component: LifestyleStep },
  { id: "savings",  Component: SavingsStep },
  { id: "reveal",   Component: RevealStep },
];

export default function OnboardingWizard() {
  const { c, upM, s } = useApp();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(ONBOARDING_DEFAULTS);

  const updateData = useCallback((updates) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const next = useCallback(() => setStep((i) => Math.min(i + 1, INPUT_STEPS.length - 1)), []);
  const back = useCallback(() => setStep((i) => Math.max(i - 1, 0)), []);

  /* Map onboarding data → app state and commit */
  const handleComplete = useCallback(() => {
    const sv = data.savingsTarget;
    upM({
      age: data.age,
      startAge: data.startAge,
      maxAge: data.maxAge,
      salary: data.salary,
      salGr: data.salGr,
      bonusR: data.bonusR,
      liv: data.liv,
      nw0: data.nw0,
      ret: data.ret,
      // Distribute savings target across age brackets (uniform for now)
      s22: sv,
      s25: Math.round(sv * 1.2),
      s30: Math.round(sv * 1.5),
      s35: Math.round(sv * 2),
      // Mark onboarding complete
      onboarded: true,
    });
  }, [data, upM]);

  const isWelcome = step === 0;
  const isReveal = step === INPUT_STEPS.length - 1;
  const { Component: CurrentStep } = INPUT_STEPS[step];
  const totalSteps = INPUT_STEPS.length;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 24px",
      background: s.dark
        ? "linear-gradient(180deg, #0A0A0A 0%, #111114 50%, #0D0D10 100%)"
        : "linear-gradient(180deg, #FAFAFA 0%, #F5F5F7 50%, #FFFFFF 100%)",
    }}>
      {/* Progress dots — hidden on welcome and reveal */}
      {!isWelcome && !isReveal && (
        <div style={{
          display: "flex", gap: 8, marginBottom: 40,
          animation: "fadeSlideUp 0.4s ease",
        }}>
          {INPUT_STEPS.slice(1, -1).map((_, i) => (
            <div key={i} style={{
              width: i === step - 1 ? 24 : 8,
              height: 8,
              borderRadius: 4,
              background: i < step ? c.blue : i === step - 1 ? c.blue : c.border,
              opacity: i <= step - 1 ? 1 : 0.4,
              transition: "all 0.3s cubic-bezier(0.25,0.1,0.25,1)",
            }} />
          ))}
        </div>
      )}

      {/* Step content */}
      <div
        key={step}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          width: "100%", maxWidth: 520,
          animation: "fadeSlideUp 0.45s cubic-bezier(0.25,0.1,0.25,1)",
        }}
      >
        <CurrentStep
          data={data}
          onChange={updateData}
          c={c}
          onNext={next}
          onComplete={handleComplete}
        />
      </div>

      {/* Navigation — hidden on welcome and reveal */}
      {!isWelcome && !isReveal && (
        <div style={{
          display: "flex", gap: 12, marginTop: 40,
          animation: "fadeSlideUp 0.5s ease 0.1s both",
        }}>
          <button onClick={back} style={{
            padding: "12px 28px", borderRadius: 12,
            border: `1px solid ${c.border}`, background: "transparent",
            color: c.muted, fontSize: 14, fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
          }}>
            Back
          </button>
          <button onClick={next} style={{
            padding: "12px 36px", borderRadius: 12, border: "none",
            background: c.blue, color: "#fff", fontSize: 14, fontWeight: 700,
            cursor: "pointer", letterSpacing: "-0.01em",
            boxShadow: `0 2px 12px ${c.blue}30`,
            transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
          }}>
            {step === totalSteps - 2 ? "See My Future" : "Continue"}
          </button>
        </div>
      )}
    </div>
  );
}
